import { MODULE_POWUR_OS_EQUIPMENT_ID } from "@logic/lib/powur-os"

import type { Contract } from "~/graphql/client"
import type { Design } from "~/utils/finance/design-types"
import { getDesignSystemSizeInWatts } from "~/utils/finance/system-size"

const CONTRACT_DIFF_EPSILON = 0.01
const WATTS_TO_KILOWATTS = 1000

export interface PendingVersionAlert {
  systemSize: number
  totalCost: number
}

const hasPendingValueChange = (
  contractValue: number | null | undefined,
  proposalValue: number | null | undefined,
): boolean => {
  // Nothing to compare against yet (e.g. proposal data still loading)
  if (typeof proposalValue !== `number`) {
    return false
  }

  // Proposal has a value but the contract doesn't — treat as changed
  if (typeof contractValue !== `number`) {
    return true
  }

  return Math.abs(contractValue - proposalValue) >= CONTRACT_DIFF_EPSILON
}

const getSentPrimaryContractForCurrentVersion = (
  contracts: Contract[],
): Contract | null =>
  contracts.find((c) => c.contractType === `primary` && !!c.dateSent) ?? null

const getCurrentSystemSizeInKwFromDesign = (
  remoteDesign: Design,
  fallbackSystemSizeInKw: number,
): number => {
  if (!remoteDesign.id) {
    return fallbackSystemSizeInKw
  }

  const calculatedSystemSizeInKw =
    getDesignSystemSizeInWatts({
      panels: remoteDesign.panels,
      installAreasToPanels: remoteDesign.installAreasToPanels,
      solarModule:
        remoteDesign.designEquipment?.find(
          ({ powurOsEquipmentTypeId }) =>
            powurOsEquipmentTypeId === MODULE_POWUR_OS_EQUIPMENT_ID,
        )?.equipment ?? null,
      source: remoteDesign.source,
      systemSizeKw: remoteDesign.systemSizeKw,
    }) / WATTS_TO_KILOWATTS

  return Number.isFinite(calculatedSystemSizeInKw)
    ? calculatedSystemSizeInKw
    : fallbackSystemSizeInKw
}

export const buildPendingVersionAlert = ({
  contracts,
  proposalTotalCost,
  remoteDesign,
}: {
  contracts: Contract[]
  proposalTotalCost: number | null | undefined
  remoteDesign: Design
}): PendingVersionAlert | null => {
  const latestSentPrimaryContract =
    getSentPrimaryContractForCurrentVersion(contracts)

  if (!latestSentPrimaryContract) {
    return null
  }

  const sentSystemSizeInKw = latestSentPrimaryContract.systemSize ?? 0
  const currentSystemSizeInKw = getCurrentSystemSizeInKwFromDesign(
    remoteDesign,
    sentSystemSizeInKw,
  )

  const hasSystemSizeChange = hasPendingValueChange(
    latestSentPrimaryContract.systemSize,
    currentSystemSizeInKw,
  )
  const hasTotalCostChange = hasPendingValueChange(
    latestSentPrimaryContract.totalCost,
    proposalTotalCost,
  )

  if (!hasSystemSizeChange && !hasTotalCostChange) {
    return null
  }

  return {
    systemSize: currentSystemSizeInKw,
    totalCost: proposalTotalCost ?? latestSentPrimaryContract.totalCost ?? 0,
  }
}
